import { type FC, type PropsWithChildren, type ReactNode } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import { useFormatter } from '@eneco/i18n';
import { Box, Divider, Stack, Stretch, Text } from '@eneco/sparky';

import { Price as PriceType } from '../../../shared/types';
import { useRibbonHeightSync } from '../../experiments/exp-n-575/components/useRibbonHeightSync';
import { useTitleHeightSync } from '../../experiments/exp-n-575/components/useTitleHeightSync';

export interface ProductCardContentProps {
  /** Prefix for title/price element ids (from `useId()` in the parent card). */
  id: string;
  title?: string;
  subtitle?: string;
  /** Headline price, shown under the title. */
  price?: PriceType;
  /** Optional second price, shown beside the headline (for example "Termijnbedrag"). */
  secondaryPrice?: PriceType;
  /** Shown under the title when there is no subtitle. */
  tag?: string;
  usps?: string[];
  /** Trailing content in the title row, such as the radio indicator. */
  titleSuffix?: ReactNode;
}

const PriceColumn: FC<{ price: PriceType; id?: string; isHeadline?: boolean }> = ({ price, id, isHeadline }) => {
  const { currency } = useFormatter();
  const formatted = currency.euro(price.value);

  return (
    <Stack direction="column" gap="0" alignX={isHeadline ? 'start' : 'end'}>
      <Text size={isHeadline ? 'BodyXL' : 'BodyM'} weight="bold">
        <span id={id} aria-label={[formatted, price.period].filter(Boolean).join(' ')}>
          {formatted}
        </span>
      </Text>
      {price.period && (
        <Text size="BodyS" color="textLowEmphasis">
          {price.period}
        </Text>
      )}
    </Stack>
  );
};

export interface ProductCardFrameProps {
  ribbon?: ReactNode;
  touRibbon?: ReactNode;
}

const CardRibbons: FC<ProductCardFrameProps> = ({ ribbon, touRibbon }) => (
  <>
    {ribbon && (
      <Box borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden">
        {ribbon}
      </Box>
    )}
    {touRibbon && !ribbon && (
      <Box borderTopLeftRadius="m" borderTopRightRadius="m" overflow="hidden">
        {touRibbon}
      </Box>
    )}
  </>
);

export const ProductCardContent: FC<PropsWithChildren<ProductCardContentProps>> = ({
  id,
  title,
  subtitle,
  price,
  secondaryPrice,
  tag,
  usps,
  titleSuffix,
  children,
}) => {
  const { titleRef, wrapperRef } = useTitleHeightSync(!!title);
  const caption = subtitle ?? tag;

  return (
    <Stretch>
      <Stack direction="column">
        <Stack.Item>
          <Box padding="6">
            <Stack gap="4">
              <Stack direction="row" alignX="justify" alignY="start" gap="2">
                <Stack direction="column" gap="0">
                  {title && (
                    <div ref={wrapperRef}>
                      <div ref={titleRef}>
                        <Text id={`${id}-label`} weight="bold">
                          {title}
                        </Text>
                      </div>
                    </div>
                  )}
                  {caption && (
                    <Text size="BodyS" color="textLowEmphasis">
                      {caption}
                    </Text>
                  )}
                </Stack>

                {titleSuffix && <Stack.Item shrink={false}>{titleSuffix}</Stack.Item>}
              </Stack>

              {price && (
                <Stack direction="row" alignX="justify" alignY="start" gap="4">
                  <PriceColumn price={price} id={`${id}-price`} isHeadline />
                  {secondaryPrice && <PriceColumn price={secondaryPrice} />}
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack.Item>

        {children && (
          <Stack.Item>
            <Divider />
            {children}
          </Stack.Item>
        )}

        {usps && usps.length > 0 && (
          <Stack.Item>
            <Divider />
            <Box padding="6">
              <Checklist>
                {usps.map(usp => (
                  <Checklist.Item key={usp} gap="2" alignY="start">
                    <Stack>
                      <RichText html={usp} />
                    </Stack>
                  </Checklist.Item>
                ))}
              </Checklist>
            </Box>
          </Stack.Item>
        )}
      </Stack>
    </Stretch>
  );
};

/** Ribbon above the card, so the card chrome does not clip it. */
export const ProductCardFrame: FC<PropsWithChildren<ProductCardFrameProps>> = ({
  ribbon,
  touRibbon,
  children,
}) => {
  const { ribbonRef, ribbonWrapperRef } = useRibbonHeightSync();

  return (
    <Stack direction="column">
      <Stack ref={ribbonWrapperRef} direction="column" alignY="end">
        <Box ref={ribbonRef}>
          <CardRibbons ribbon={ribbon} touRibbon={touRibbon} />
        </Box>
      </Stack>

      <Stack.Item grow>
        <Stretch>
          <Stack direction="row">
            <Stretch>{children}</Stretch>
          </Stack>
        </Stretch>
      </Stack.Item>
    </Stack>
  );
};
